import { FfsResultType } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'
import { GeckoMeasurement } from 'types'

import { conservativeMinPressure, DeratePressure } from './ffsPressure'

/**
 * The severity model, and the single aggregation step every scope shares.
 *
 * Split out from the rollup itself so that "which results are verdicts" has ONE
 * definition. It previously existed twice — as a `Set` in the selection module
 * and as the key set of the severity order here — which meant adding a result
 * type required remembering both. Selection also needs the predicate, and the
 * rollup needs selection, so a leaf module is what breaks the cycle.
 */

/** The values that can be signed off. `pending`/`skipped` are states, not ranks. */
export type Verdict =
  | FfsResultType.Pass
  | FfsResultType.Derate
  | FfsResultType.Fail

/**
 * Fitness-for-service is decided by the worst case, so every scope inherits the
 * highest-ranked verdict beneath it.
 */
export const FFS_SEVERITY_ORDER: Record<Verdict, number> = {
  [FfsResultType.Pass]: 0,
  [FfsResultType.Derate]: 1,
  [FfsResultType.Fail]: 2,
}

export const worseOf = (a: Verdict, b: Verdict): Verdict =>
  FFS_SEVERITY_ORDER[a] >= FFS_SEVERITY_ORDER[b] ? a : b

/** Derived from the order table, so the two can never disagree. */
export const isVerdict = (result: FfsResultType): result is Verdict =>
  result in FFS_SEVERITY_ORDER

const FFS_RESULT_TYPES: ReadonlySet<string> = new Set<string>(
  Object.values(FfsResultType)
)

/**
 * Narrows an arbitrary string to `FfsResultType`.
 *
 * Lives here, in a leaf module, because three places need it: parsing a saved
 * report, rendering a stored classification that may hold a literal like
 * `'UNKNOWN'`, and rendering a recorded close-out result. It previously existed
 * twice — once as a `Set` in the parser and once as an `in RESULT_OUTCOME_COLORS`
 * check inside a step COMPONENT, which meant importing that step's entire UI
 * graph just to borrow a two-line predicate.
 */
export const isFfsResultType = (value: string): value is FfsResultType =>
  FFS_RESULT_TYPES.has(value)

/** One child of a scope — an LTA within a component, or a component within a unit. */
export interface VerdictChild {
  id: string
  result: Verdict
  /**
   * The child's re-rate pressure. `null` covers both "has none" and "had one but
   * it was unavailable", because both make the parent's aggregate unavailable.
   */
  pressure: GeckoMeasurement | null
}

export interface VerdictAggregate {
  result: Verdict
  /** Every child at the winning severity — ties are all recorded, never collapsed. */
  governingIds: string[]
  /** Meaningful only when `result` is `derate`; `null` otherwise. */
  derate: DeratePressure | null
}

/**
 * Reduce a scope's verdict-bearing children to one verdict.
 *
 * Identical at every level, which is why it lives here rather than being written
 * once per scope: an LTA rolling up into a component and a component rolling up
 * into a unit are the same operation over different accessors.
 *
 * `null` for an empty input, so a caller cannot accidentally synthesise a verdict
 * from nothing — that case is `no-verdict`, which is not the same as a Pass.
 */
export function aggregateVerdicts(
  children: ReadonlyArray<VerdictChild>
): VerdictAggregate | null {
  if (children.length === 0) return null

  const result = children.reduce<Verdict>(
    (worst, child) => worseOf(worst, child.result),
    FfsResultType.Pass
  )
  const winners = children.filter((child) => child.result === result)

  return {
    result,
    governingIds: winners.map((child) => child.id),
    // Only a derate carries a pressure, and it must be the LOWEST across every
    // derating child -- a subset minimum would understate the required re-rate.
    derate:
      result === FfsResultType.Derate
        ? conservativeMinPressure(winners.map((child) => child.pressure))
        : null,
  }
}
